<template>
  <section id="topics" class="section section-observed topic-section cms-layout-standard">
    <div v-if="front.activeBlocks.length && !isPagedListPage" class="cms-blocks">
      <BlockRenderer
        v-for="block in front.activeBlocks"
        :key="block.blockId || block.blockCode"
        :block="block"
        class="cms-block"
      />
    </div>

    <div v-if="isPagedListPage" class="international-list-card">
      <div class="international-list-count">共 {{ total }} 篇</div>
      <div v-if="loading" class="muted-copy">正在加载...</div>
      <div v-else-if="listItems.length" class="international-list">
        <div
          v-for="(item, index) in listItems"
          :key="item.entryId || item.title"
          class="international-list-item-wrap"
        >
          <button
            type="button"
            class="international-list-item"
            @click="front.openCmsEntry(item)"
          >
            <div class="article-index" aria-label="序号">
              <strong>{{ itemIndex(index) }}</strong>
            </div>
            <div class="article-main">
              <div class="article-meta">
                <span>{{ item.category || front.moduleLabel(item.moduleCode) }}</span>
                <time>{{ formatDate(item.date) }}</time>
              </div>
              <h4>{{ item.title }}</h4>
              <p>{{ item.summary || '暂无摘要' }}</p>
            </div>
            <span class="article-arrow">阅读全文</span>
          </button>
          <div v-if="front.entryAttachments(item).length" class="international-list-files">
            <button
              v-for="(file, fileIndex) in front.entryAttachments(item)"
              :key="file.id || file.url || fileIndex"
              type="button"
              class="cms-file-link"
              @click="front.downloadEntryAttachment(item, file)"
            >
              {{ front.attachmentLabel(file, fileIndex, front.entryAttachments(item).length) }}
            </button>
          </div>
        </div>
      </div>
      <p v-else class="muted-copy">暂无已发布内容</p>
      <FrontPagination
        :page-num="pageNum"
        :page-size="pageSize"
        :total="total"
        @change="onPageChange"
      />
    </div>

    <div
      v-else
      class="topic-groups"
      :class="{ 'topic-groups--single': front.cmsFeatureGroups.length <= 1 }"
    >
      <article
        v-for="group in front.cmsFeatureGroups"
        :id="`topic-${group.moduleCode}`"
        :key="group.moduleCode"
        class="topic-group topic-anchor"
      >
        <div class="topic-group-head">
          <span>{{ group.scopeLabel }}</span>
          <h3>{{ group.label }}</h3>
        </div>
        <div class="topic-list">
          <button v-for="item in group.items" :key="item.entryId || item.title" type="button" @click="front.openCmsEntry(item)">
            <strong>{{ item.title }}</strong>
            <span>{{ item.summary || item.category || item.date || '暂无摘要' }}</span>
          </button>
          <p v-if="!group.items.length" class="muted-copy">暂无已发布内容</p>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import { listPublicCmsEntries, unwrapPublicPage, applyClientPageFallback } from '../../../api/paleo'
import FrontPagination from '../../common/FrontPagination.vue'
import BlockRenderer from '../blocks/BlockRenderer.vue'

export default {
  name: 'StandardLayout',
  components: { BlockRenderer, FrontPagination },
  inject: ['front'],
  data() {
    return {
      pageNum: 1,
      pageSize: 5,
      total: 0,
      listItems: [],
      loading: false,
      loadSeq: 0
    }
  },
  computed: {
    isInternationalPage() {
      return this.front.page === 'international'
    },
    isRegulationsPage() {
      return this.front.page === 'regulations'
    },
    isPagedListPage() {
      return this.isInternationalPage || this.isRegulationsPage
    },
    listModuleCode() {
      if (this.isInternationalPage) return 'international'
      if (this.isRegulationsPage) return 'regulations'
      return ''
    }
  },
  watch: {
    'front.page': {
      immediate: true,
      handler(val) {
        if (val === 'international' || val === 'regulations') {
          this.pageNum = 1
          this.loadList()
        }
      }
    }
  },
  methods: {
    itemIndex(index) {
      return (this.pageNum - 1) * this.pageSize + index + 1
    },
    onPageChange(page) {
      this.pageNum = page
      this.loadList()
      this.$nextTick(() => this.front.scrollTo && this.front.scrollTo('topics'))
    },
    async loadList() {
      if (!this.listModuleCode) return
      const seq = ++this.loadSeq
      this.loading = true
      try {
        const result = await listPublicCmsEntries({
          moduleCode: this.listModuleCode,
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        if (seq !== this.loadSeq) return
        const { rows, total } = unwrapPublicPage(result)
        const mapped = rows
          .map(item => this.front.mapCmsEntry(item))
          .sort((a, b) => String(b.publishTime || b.createTime || '').localeCompare(
            String(a.publishTime || a.createTime || '')
          ))
        const paged = applyClientPageFallback(mapped, total, this.pageNum, this.pageSize)
        this.listItems = paged.rows
        this.total = paged.total
      } catch (error) {
        if (seq !== this.loadSeq) return
        if (!this.listItems.length) {
          this.listItems = []
          this.total = 0
        }
      } finally {
        if (seq === this.loadSeq) this.loading = false
      }
    },
    formatDate(date) {
      const raw = String(date || '').trim()
      return raw ? raw.slice(0, 10) : '待发布'
    }
  }
}
</script>

<style scoped>
.layout-subtitle {
  margin: 12px 0 0;
  color: var(--subtle);
  line-height: 1.8;
  max-width: 780px;
}

.international-list-card {
  padding: 28px;
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  background: #fff;
  box-shadow: var(--shadow-soft);
}

.international-list-count {
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border);
  color: var(--subtle);
  font-size: 14px;
  text-align: right;
}

.international-list {
  display: grid;
  gap: 14px;
}

.international-list-item {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr) auto;
  gap: 18px;
  align-items: center;
  width: 100%;
  padding: 18px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fbfdff;
  color: inherit;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.international-list-item:hover {
  border-color: rgba(26, 90, 147, 0.35);
  box-shadow: 0 14px 34px rgba(0, 29, 54, 0.08);
  transform: translateY(-2px);
}

.article-index {
  display: grid;
  place-items: center;
  min-width: 68px;
  min-height: 68px;
  border-radius: 8px;
  background: linear-gradient(180deg, #eef6ff, #f8fbff);
  color: var(--primary);
}

.article-index strong {
  font-size: 26px;
  line-height: 1;
  font-weight: 800;
}

.article-main {
  min-width: 0;
}

.article-meta {
  display: flex;
  gap: 12px;
  margin-bottom: 7px;
  color: var(--subtle);
  font-size: 13px;
}

.article-main h4 {
  margin: 0;
  color: var(--ink);
  font-size: 18px;
}

.article-main p {
  margin: 8px 0 0;
  color: var(--subtle);
  line-height: 1.7;
}

.article-arrow {
  color: var(--primary);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

.muted-copy {
  margin: 0;
  color: var(--subtle);
  line-height: 1.7;
}

@media (max-width: 720px) {
  .international-list-card {
    padding: 18px;
  }

  .international-list-item {
    grid-template-columns: 1fr;
  }

  .article-index {
    display: inline-flex;
    justify-content: center;
    gap: 8px;
    width: fit-content;
    min-height: auto;
    padding: 8px 12px;
  }
}
</style>
